import { useEffect, useState, useMemo, useCallback, useRef } from 'react';

import FormField from '../../FormField';

export default function RadioButtonField(
    {value = false, isDisabled, extraInfo, name, label, fieldProps,
    setFieldValue, confirmationButtonLabel, declineButtonLabel, tooltip, onError, 
    getOptions, id, fieldKey, request}
) {

    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const optionsCache = useRef(new Map());

    const hasCustomOptions = options.length > 0;

    const findOptionByValue = useCallback((searchValue) => {
        return options.find(option => 
            String(option.id || option.value) === String(searchValue)
        );
    }, [options]);

    const getCacheKey = useCallback((id, name, areaId, fieldKey) => {
        return `${id}_${name}_${areaId || 'null'}_${fieldKey || 'null'}`;
    }, []);

    useEffect(() => {
        if (getOptions && id) {
            const cacheKey = getCacheKey(id, name, request?.areaId, fieldKey);
            const cachedOptions = optionsCache.current.get(cacheKey);

            if (cachedOptions) {
                setOptions(cachedOptions);
                return;
            }

            const shouldLoadOptions = !hasCustomOptions || 
                (value && !findOptionByValue(value) && !isLoadingOptions);

            if (shouldLoadOptions && !isLoadingOptions) {
                const loadOptions = async () => {
                    setIsLoading(true);
                    setIsLoadingOptions(true);
                    try {
                        const response = await getOptions(id, name, request?.areaId, fieldKey);
                        const optionsData = response?.data || [];
                        setOptions(optionsData);
                        optionsCache.current.set(cacheKey, optionsData);
                    } catch (error) {
                        setOptions([]);
                    } finally {
                        setIsLoading(false);
                        setIsLoadingOptions(false);
                    }
                };
                loadOptions();
            }
        }
    }, [getOptions, id, name, request?.areaId, fieldKey, value, hasCustomOptions, findOptionByValue, isLoadingOptions, getCacheKey]);

    const fieldOptions = useMemo(() => {
        return hasCustomOptions
            ? options.map(option => ({ 
                name: option.name || option.label, 
                value: String(option.id || option.value)
            }))
            : [
                { name: confirmationButtonLabel || "Sim", value: true },
                { name: declineButtonLabel || "Não", value: false },
            ];
    }, [hasCustomOptions, options, confirmationButtonLabel, declineButtonLabel]);

    const currentValue = useMemo(() => {
        if (hasCustomOptions) {
            const searchValue = value || ((request?.fields || {})[name]?.values?.[0]?.value || (request?.fields || {})[name]?.value);
            const matchingOption = findOptionByValue(searchValue);
            return matchingOption ? String(matchingOption.id || matchingOption.value) : "";
        }
        if (value === null || value === undefined) return null;
        return value === true;
    }, [hasCustomOptions, value, request?.fields, name, findOptionByValue]);

    const handleChange = useCallback((name, value) => {
        if (hasCustomOptions) {
            const selectedOption = findOptionByValue(value);
            const optionId = selectedOption?.id || selectedOption?.value;
            setFieldValue(name, optionId);
        } else {
            setFieldValue(name, value === "true");
        }
    }, [hasCustomOptions, findOptionByValue, setFieldValue]);


    return (
        <FormField
            titleSize={15}
            titleWeight={600}
            spacing={10}
            {...fieldProps}
            type='radioButton'
            name={name}
            currentValue={currentValue}
            disabled={isDisabled || fieldProps?.disabled || isLoading}
            helperText={extraInfo}
            field={label}
            tooltip={tooltip}
            onChange={handleChange}
            options={fieldOptions}
            onError={onError}
            isLoading={isLoading}
        />
    );
}
